// --------------------------------------------------------------
// Created On: 2025-04-24
// Author: Zachary Thomas
//
// Last Modified: 2025-06-12
// Modified By: Zachary Thomas
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------
import deepCopy from "../deepCopy";

// Given a specific series index, this returns all series that share the same y-axis.
export default function getSeriesThatShareYAxis<
  Type extends AccumulationDataSeries | AccumulationDataSeriesWithMaxValue | TimeDataSeries
>(
  seriesIndex: number,
  seriesUnitName: string,
  seriesUnitMap: SeriesUnitMap,
  axisMode: AxisMode,
  series: Type[]
): Type[] {
  const seriesDeepCopy = deepCopy(series);
  const seriesThatShareYAxis = [];
  switch (axisMode) {
    case "Y_AXIS_ATTRIBUTE":
      if (seriesIndex < seriesDeepCopy.length) {
        seriesThatShareYAxis.push(seriesDeepCopy[seriesIndex]);
      }
      break;
    case "Y_AXIS_UOM": {
      // Find all series that share the same unit name.
      if (seriesUnitName in seriesUnitMap) {
        const sharedSeriesIndexes = Object.keys(seriesUnitMap[seriesUnitName]);
        sharedSeriesIndexes.forEach((indexString) => {
          const index = parseInt(indexString, 10);
          if (index < seriesDeepCopy.length) {
            seriesThatShareYAxis.push(seriesDeepCopy[index]);
          }
        });
      }
      break;
    }
  }
  return seriesThatShareYAxis as Type[];
}

interface SeriesUnitMap {
  [key: string]: {
    // The dynamic key is the unit name.
    [key: string]: {
      // The dynamic key is the series index number.
      thisSeriesDrawsAxis: boolean; // Whether this specific series is generating the y-axis.
    };
  };
}

interface TimeDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: [number, number][];
}

interface AccumulationDataSeries {
  name: string;
  unitName: string;
  unitShortName: string;
  data: number[];
}

interface AccumulationDataSeriesWithMaxValue {
  name: string;
  unitName: string;
  unitShortName: string;
  data: number[];
  maxValue: number | null;
}

type AxisMode = "Y_AXIS_ATTRIBUTE" | "Y_AXIS_UOM";
